import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, useTheme } from "@mui/material";
import { type RecentColleague } from "@src/appV2/Agents/api/useGetRecentColleagues";
import { LoadingSpinner, Text } from "@src/appV2/redesign/system/ui-components";
import { type RefObject } from "react";
import { Virtuoso } from "react-virtuoso";

import { OpenShiftCardContainer } from "../../Shift/Open/CardContainer";
import type { OpenShift, OpenShiftWorkplace } from "../../Shift/Open/types";
import { useOpenShiftListDataContext } from "../../Shift/Open/useOpenShiftListDataContext";
import { GOLDEN_RATIO_BOTTOM_FLEX, GOLDEN_RATIO_TOP_FLEX } from "../../utils/goldenRatioLayout";
import { type WorkerPublicProfile } from "../../WorkWithFriends/hooks/useGetWorkerPublicProfile";

// Travel for more shifts does not surface colleague/friend context on shift cards
// — pass empty references at module scope so each row doesn't get fresh identities per render.
const EMPTY_WORKERS: WorkerPublicProfile[] = [];
const EMPTY_FRIENDS_MAP: Map<string, WorkerPublicProfile> = new Map();
const EMPTY_RECENT_COLLEAGUES_MAP: Map<string, RecentColleague> = new Map();

interface TravelForMoreShiftsListProps {
  scrollRef: RefObject<HTMLDivElement>;
  shifts: OpenShift[];
  workplacesMap: Map<string, OpenShiftWorkplace>;
}

export function TravelForMoreShiftsList(props: TravelForMoreShiftsListProps) {
  const { scrollRef, shifts, workplacesMap } = props;
  const theme = useTheme();
  const { onClickOpenShift, isOpenShiftsLoading, isOpenShiftsError, isOpenShiftsSuccess } =
    useOpenShiftListDataContext();

  if (isOpenShiftsError) {
    return (
      <Stack ref={scrollRef} sx={{ flex: 1, overflowY: "auto", paddingX: 5, paddingY: 8 }}>
        <Text align="center">
          Something went wrong loading shifts further away. Pull to refresh to try again.
        </Text>
      </Stack>
    );
  }

  if (isOpenShiftsLoading) {
    return (
      <Stack ref={scrollRef} sx={{ flex: 1, overflowY: "auto", paddingX: 5 }}>
        <Box sx={{ flex: GOLDEN_RATIO_TOP_FLEX, minHeight: 0 }} />
        <Stack alignItems="center">
          <LoadingSpinner size="medium" variant="primary" />
        </Stack>
        <Box sx={{ flex: GOLDEN_RATIO_BOTTOM_FLEX, minHeight: 0 }} />
      </Stack>
    );
  }

  if (isOpenShiftsSuccess && shifts.length === 0) {
    return (
      <Stack ref={scrollRef} sx={{ flex: 1, overflowY: "auto", paddingX: 5, paddingY: 8 }}>
        <Text align="center">No shifts found at this distance.</Text>
      </Stack>
    );
  }

  return (
    <Box
      ref={scrollRef}
      data-testid="travel-for-more-shifts-list"
      sx={{
        flex: 1,
        overflowY: "auto",
        backgroundColor: theme.palette.background.tertiary,
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <Virtuoso
        customScrollParent={scrollRef.current ?? undefined}
        data={shifts}
        components={{
          Header: () => (
            <Text
              variant="body2"
              sx={(t) => ({
                color: t.palette.text.secondary,
                paddingX: 5,
                paddingY: 5,
              })}
            >
              Shifts over 150 miles away
            </Text>
          ),
        }}
        itemContent={(_index, shift) => {
          const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
          if (!isDefined(workplace)) {
            return null;
          }

          return (
            <Box sx={{ paddingTop: 5 }}>
              <OpenShiftCardContainer
                isFromNearestOpenShifts
                shift={shift}
                workplace={workplace}
                blockingRequirementsContext={undefined}
                workers={EMPTY_WORKERS}
                friendsMap={EMPTY_FRIENDS_MAP}
                recentColleaguesMap={EMPTY_RECENT_COLLEAGUES_MAP}
                source="travelForMoreShifts"
                sx={{ marginX: 5 }}
                onClick={({ offer }) => {
                  onClickOpenShift({
                    shift,
                    workplace,
                    offer,
                    source: "travelForMoreShifts",
                  });
                }}
              />
            </Box>
          );
        }}
      />
    </Box>
  );
}
